import * as React from 'react';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertBanner, Button, Card, GovernmentIdentity, TextInput } from '@ministry/ui/internal';

import { login } from '../lib/auth.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { SESSION_QUERY_KEY } from '../lib/session.js';
import { PasswordField } from '../components/PasswordField.js';
import { useT } from '../i18n/strings.js';
import { LocaleToggle } from '../components/LocaleToggle.js';

interface LocationState {
  from?: Location;
}

/**
 * SCREEN_SPECS.md I1. All four async states: idle, submitting (button `loading`),
 * error (several distinct causes below), and success (navigates away — this screen has
 * no "populated" resting state of its own).
 *
 * The error copy is deliberately GENERIC on invalid credentials — API_CONTRACT.md's
 * `AUTH_INVALID_CREDENTIALS` never says which of username/password was wrong, and this
 * screen must not undo that by being more specific than the server is.
 */
export function LoginPage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [codePrefix, setCodePrefix] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [touched, setTouched] = React.useState<{ codePrefix?: boolean; username?: boolean; password?: boolean }>({});

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (result) => {
      // Invalidate, never seed. `/auth/login` returns a THINNER shape than `/auth/me` —
      // the user id and role codes only — so writing it into the session cache made every
      // consumer read a half-populated session and `/auth/me` was never fetched. The
      // dashboard rendered "Welcome, {name}" with no error anywhere, because a missing
      // field is not a failure in React, it is a blank.
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/dashboard';
      // SCREEN_SPECS.md I3 (forced password change) is not built this round — the flag
      // is threaded through as router state so the dashboard can at least surface it
      // rather than silently dropping it. See the handback for this limitation.
      navigate(redirectTo, { replace: true, state: { mustChangePassword: result.mustChangePassword } });
    },
  });

  const codePrefixError =
    touched.codePrefix && codePrefix.trim() === ''
      ? t('login.required')
      : touched.codePrefix && !/^[A-Za-z]{2,6}$/.test(codePrefix.trim())
        ? t('login.ministry.invalid')
        : undefined;
  const usernameError = touched.username && username.trim() === '' ? t('login.required') : undefined;
  const passwordError = touched.password && password === '' ? t('login.required') : undefined;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ codePrefix: true, username: true, password: true });
    if (!/^[A-Za-z]{2,6}$/.test(codePrefix.trim()) || username.trim() === '' || password === '') return;
    mutation.mutate({ codePrefix: codePrefix.trim().toUpperCase(), username: username.trim(), password });
  };

  const error = mutation.error;
  let errorBanner: React.ReactNode = null;

  if (error) {
    if (error instanceof ApiError && error.code === 'AUTH_MFA_REQUIRED') {
      // Handled by the effect below (navigates away) — no banner needed on this pass.
    } else if (error instanceof ApiError && error.code === 'ACCOUNT_LOCKED') {
      const retryAfterSeconds = typeof error.details?.retryAfterSeconds === 'number' ? error.details.retryAfterSeconds : 300;
      const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
      errorBanner = (
        <AlertBanner variant="critical" title={t('login.error.locked', { minutes })} />
      );
    } else if (error instanceof ApiError && error.code === 'RATE_LIMITED') {
      errorBanner = <AlertBanner variant="warning" title={t('login.error.rateLimited')} />;
    } else if (error instanceof ApiError && error.code === 'AUTH_INVALID_CREDENTIALS') {
      errorBanner = <AlertBanner variant="critical" title={t('login.error.invalidCredentials')} />;
    } else if (error instanceof NetworkError || (error instanceof ApiError && error.retryable)) {
      errorBanner = (
        <AlertBanner
          variant="critical"
          title={t('login.error.network')}
          action={
            <Button size="sm" variant="secondary" onClick={() => mutation.mutate({ codePrefix: codePrefix.trim().toUpperCase(), username: username.trim(), password })}>
              {t('common.retry')}
            </Button>
          }
        />
      );
    } else {
      // API_CONTRACT.md §4.3: an unrecognised code falls back to a generic message —
      // never `error.message` (English, developer-only, never rendered to a user).
      errorBanner = <AlertBanner variant="critical" title={t('login.error.generic')} />;
    }
  }

  React.useEffect(() => {
    if (error instanceof ApiError && error.code === 'AUTH_MFA_REQUIRED') {
      const mfaChallengeId = error.details?.mfaChallengeId;
      if (typeof mfaChallengeId === 'string') {
        navigate('/mfa', { state: { mfaChallengeId, username: username.trim() } });
      }
    }
  }, [error, navigate, username]);

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      {/*
        ux-review defect (chrome pass): the language toggle used to float
        right-aligned above the card, anchored to nothing — it read as orphaned
        rather than chrome. It now lives inside a real header bar, the same row as
        the wordmark/emblem lockup (login and MFA render outside AppShell, so this is
        the only chrome either of those screens has, and the first screens a staff
        member sees should carry the portal's identity, not just a bare form).
      */}
      <header className="flex items-center justify-between gap-4 px-4 py-3">
        <GovernmentIdentity />
        <LocaleToggle />
      </header>

      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4">
        <Card className="w-full max-w-sm p-6">
          <h1 className="mb-4 text-xl font-semibold text-text-heading">{t('login.title')}</h1>

          {errorBanner ? <div className="mb-4">{errorBanner}</div> : null}

          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            {/*
              The ministry prefix, NOT a dropdown. A select would need an anonymous endpoint
              listing every ministry on the platform, which hands an unauthenticated visitor
              the tenant roster — the same enumeration-oracle this screen's generic
              credential error exists to avoid. Staff know their own prefix; it is the one
              on every tracking code they handle.
            */}
            <TextInput
              id="login-ministry"
              label={t('login.ministry.label')}
              name="organization"
              autoComplete="organization"
              autoCapitalize="characters"
              value={codePrefix}
              onChange={(event) => setCodePrefix(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, codePrefix: true }))}
              errorText={codePrefixError}
              helperText={t('login.ministry.hint')}
              required
            />
            <TextInput
              id="login-username"
              label={t('login.username.label')}
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, username: true }))}
              errorText={usernameError}
              required
            />
            <PasswordField
              id="login-password"
              label={t('login.password.label')}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
              errorText={passwordError}
              required
            />
            <Button type="submit" loading={mutation.isPending} loadingLabel={t('login.submitting')}>
              {t('login.submit')}
            </Button>
          </form>

          {/*
            I4's own screen now exists (`PasswordResetRequestPage`) — this link went
            unrendered before that was true, on the "a visible control that does nothing"
            rule this build's brief names directly; it earns its place on the page the
            moment it has somewhere real to go.
          */}
          <Link to="/reset-password/request" className="mt-4 inline-block text-sm font-semibold text-text-link underline">
            {t('login.forgotPassword')}
          </Link>
        </Card>
      </div>
    </div>
  );
}
